/**
 * honorairesModel.js — SOURCE UNIQUE du modèle d'honoraires (calcul + affichage).
 *
 * Consommé par : saisie (`PropertyEditPage.jsx`), fiche consultation
 * (`PropertyViewContent.jsx`), fiche publique (`PropertyPublicPage.jsx`).
 * Miroir Python pour les PDF : `backend/pdf_generator.py::_honoraires_view`.
 * Projection publique : `backend/server.py::_public_honoraires_fields`.
 *
 * ═══ RÈGLE MÉTIER (validée client, iter123) ═══════════════════════════════
 *  Le PRIX NET VENDEUR est la donnée pivot : c'est la seule donnée de prix saisie.
 *  Le taux s'applique TOUJOURS au net vendeur — jamais au prix FAI.
 *      net vendeur                      180 000,00
 *      honoraires TTC = net × taux       18 000,00   (10 %)
 *      honoraires HT  = TTC ÷ 1,20       15 000,00
 *      TVA            = TTC − HT          3 000,00
 *      PRIX FAI       = net + TTC       198 000,00   ← toujours CALCULÉ, jamais saisi
 *  Variante forfait : 180 000 + 20 000 TTC → HT 16 666,67 · TVA 3 333,33 · FAI 200 000.
 *  ⚠️ iter121 calculait le taux sur le prix de vente (FAI) : règle ABANDONNÉE.
 *  Les deux modes de saisie (montant TTC ou pourcentage) sont également valides :
 *  un barème d'agence s'exprime en %, une licence est un forfait.
 *
 *  LOCATION (mise en location) : la commission est due UNE SEULE FOIS à la signature
 *  du bail. Elle N'EST PAS mensuelle. Base de calcul = loyer ANNUEL HT. Les honoraires
 *  réellement mensuels relèvent de la gestion locative (mandat distinct, hors Komerces)
 *  → champ SÉPARÉ à créer, ne jamais réutiliser `honoraires_location_fixe`.
 *
 *  Champs persistés (déclarés côté Pydantic — sinon silencieusement ignorés) :
 *  `net_vendeur` (pivot, vente), `honoraires_ttc`, `honoraires_ht`,
 *  `honoraires_tva_montant`, `honoraires_taux_tva`, `honoraires_pourcentage`,
 *  `honoraires_charge`, `honoraires_type`. `prix_cession` est tenu à jour = PRIX FAI
 *  (lecture seule à l'écran) pour toutes les surfaces aval (listes, recherche, export).
 *  `prix_annonce` est un prix marketing distinct : JAMAIS utilisé dans ces calculs.
 *
 * ═══ RÈGLE D'AFFICHAGE PUBLIC (simplification client, iter124) ════════════
 *  Vente/cession, charge ACQUÉREUR  → prix du bien (net vendeur) en tête +
 *                                     décomposition complète (colonne de droite, en bas)
 *  Vente/cession, charge VENDEUR    → prix FAI + mention seule + lien barème
 *  Vente/cession, charge PARTAGÉS   → prix FAI + mention seule + lien barème
 *  LOCATION de local commercial     → montant des honoraires + charge + lien barème,
 *                                     SANS ventilation (pas de HT/TVA/TTC/taux)
 *  LICENCE (vente ET location)      → montant + lien barème, TOUJOURS, quelle que soit la charge
 *  Charge NON renseignée            → prix FAI seul : aucune mention, aucun « N/C », aucun bloc vide
 *  → `publicHonorairesMode()` est la seule porte d'entrée de cette règle.
 * ═════════════════════════════════════════════════════════════════════════
 */

export const TVA_DEFAULT = 20;

export const LOCATION_OPERATIONS = ['location', 'location_droit_entree'];

export const isLocationOperation = (operation) => LOCATION_OPERATIONS.includes(operation);

export const CHARGE_OPTIONS = (isLoc) => (isLoc
  ? [{ v: 'acquereur', l: 'Preneur (locataire)' }, { v: 'vendeur', l: 'Bailleur' }]
  : [{ v: 'acquereur', l: 'Acquéreur' }, { v: 'vendeur', l: 'Vendeur' }]);

export const chargeLabel = (charge, isLoc) => {
  const map = isLoc
    ? { acquereur: 'Preneur (locataire)', preneur: 'Preneur (locataire)', vendeur: 'Bailleur', bailleur: 'Bailleur' }
    : { acquereur: 'Acquéreur', vendeur: 'Vendeur' };
  return map[String(charge || '').toLowerCase()] || charge || null;
};

/** Vrai si l'acquéreur/preneur paie les honoraires. */
export const isChargeAcquereur = (charge) => ['acquereur', 'preneur'].includes(String(charge || '').toLowerCase());

/**
 * iter124 — la décomposition chiffrée n'est publique QUE sur une vente/cession
 * dont les honoraires sont à la charge de l'acquéreur.
 */
export const chargeShowsDetail = (charge) => isChargeAcquereur(charge);

/**
 * Mode d'affichage public des honoraires (règle client iter124) :
 *   'detail'  vente/cession, charge acquéreur → décomposition complète (colonne de droite, en bas)
 *   'montant' location de local OU licence (vente & location) → montant seul + charge, sans ventilation
 *   'mention' vente/cession, charge vendeur ou partagés → mention seule, aucun montant
 *   'none'    charge non renseignée et rien à annoncer → prix FAI seul, aucun bloc
 * @param {object} hv résultat de `deriveHonorairesView`
 * @param {boolean} isLicence licence de débit de boissons (vendue ou louée seule)
 */
export function publicHonorairesMode(hv, isLicence = false) {
  if (!hv) return 'none';
  const hasCharge = !!hv.charge;
  if (isLicence || hv.isLoc) return hv.ttc != null ? 'montant' : (hasCharge ? 'mention' : 'none');
  if (isChargeAcquereur(hv.charge)) return 'detail';
  return hasCharge ? 'mention' : 'none';
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const r2 = (n) => (n == null ? null : Math.round(n * 100) / 100);

/**
 * Calcul bidirectionnel : quel que soit le champ saisi, tout est recalculé.
 * Base du taux : NET VENDEUR (vente) / loyer ANNUEL HT (location).
 * @param {'ht'|'ttc'|'pct'} source champ saisi
 * @param {?number} pnv prix net vendeur — donnée pivot en vente
 * @param {?number} loyerAnnuel loyer annuel HT — base du taux en location
 */
export function computeHonoraires({ isLoc = false, pnv = null, loyerAnnuel = null, tva = TVA_DEFAULT, source, value }) {
  const taux = num(tva) ?? TVA_DEFAULT;
  const coef = 1 + taux / 100;
  const v = num(value);
  let ht = null, ttc = null, pct = null;

  const netVendeur = isLoc ? null : num(pnv);
  const base = isLoc ? num(loyerAnnuel) : netVendeur;

  if (source === 'ht' && v != null) { ht = v; ttc = r2(v * coef); }
  else if (source === 'ttc' && v != null) { ttc = v; ht = r2(v / coef); }
  else if (source === 'pct' && v != null) {
    pct = v;
    if (base != null) { ttc = r2((base * v) / 100); ht = r2(ttc / coef); }
  }

  if (pct == null && ttc != null && base) pct = r2((ttc / base) * 100);

  const tva_montant = (ht != null && ttc != null) ? r2(ttc - ht) : null;
  // PRIX FAI : toujours calculé (net vendeur + honoraires TTC), jamais saisi.
  const fai = (!isLoc && netVendeur != null && ttc != null) ? r2(netVendeur + ttc) : null;

  return {
    ht, ttc, pct, tva_montant, tva_taux: taux,
    pnv: netVendeur,
    fai,
    base: base ?? null,
  };
}

/**
 * Lit un bien et renvoie TOUTES les grandeurs d'honoraires, en complétant
 * ce qui manque (biens migrés / legacy). Ne modifie pas le bien.
 */
export function deriveHonorairesView(property) {
  const p = property || {};
  const operation = (p.taxonomy && p.taxonomy.operation) || p.operation;
  const isLoc = isLocationOperation(operation);
  const vente = p.informations_vente || {};
  const bail = p.informations_bail || {};
  const legacy = p.honoraires_calcul || {};
  const src = isLoc ? bail : vente;

  const tva = num(src.honoraires_taux_tva) ?? TVA_DEFAULT;
  const charge = src.honoraires_charge || null;

  // Base de calcul : loyer ANNUEL HT (location) / NET VENDEUR (vente)
  const loyerAnnuel = isLoc
    ? (num(bail.loyer_annuel) ?? (num(bail.loyer_mensuel) != null ? num(bail.loyer_mensuel) * 12 : null))
    : null;
  // `prix_cession` = PRIX FAI (calculé) — utilisé seulement pour reconstituer le net
  // vendeur des biens legacy. `prix_annonce` (prix marketing) n'entre JAMAIS ici.
  const faiStocke = isLoc ? null : (num(vente.prix_cession) ?? num(legacy.prix_vente));

  const storedTtc = isLoc
    ? (num(src.honoraires_ttc) ?? num(bail.honoraires_location_fixe))
    : (num(src.honoraires_ttc) ?? num(vente.honoraires_fixe) ?? num(legacy.honoraires));
  const storedHt = num(src.honoraires_ht);
  const storedPct = isLoc
    ? (num(src.honoraires_pourcentage) ?? num(bail.honoraires_location_pourcentage))
    : (num(src.honoraires_pourcentage) ?? num(legacy.taux));

  // Net vendeur = donnée pivot. Reconstitution legacy : FAI stocké − honoraires TTC.
  let pnv = isLoc ? null : (num(vente.net_vendeur) ?? num(legacy.net_vendeur));
  if (!isLoc && pnv == null && faiStocke != null && storedTtc != null && storedTtc < faiStocke) {
    pnv = r2(faiStocke - storedTtc);
  }

  // Priorité de reconstitution des honoraires : TTC > HT > %
  let computed;
  if (storedTtc != null) computed = computeHonoraires({ isLoc, pnv, loyerAnnuel, tva, source: 'ttc', value: storedTtc });
  else if (storedHt != null) computed = computeHonoraires({ isLoc, pnv, loyerAnnuel, tva, source: 'ht', value: storedHt });
  else if (storedPct != null) computed = computeHonoraires({ isLoc, pnv, loyerAnnuel, tva, source: 'pct', value: storedPct });
  else computed = computeHonoraires({ isLoc, pnv, loyerAnnuel, tva, source: 'none', value: null });

  const hasAny = computed.ttc != null || computed.ht != null || computed.pct != null || !!charge;

  return {
    isLoc,
    charge,
    chargeLabel: chargeLabel(charge, isLoc),
    showDetail: chargeShowsDetail(charge),
    type: isLoc ? (bail.honoraires_type || null) : (vente.honoraires_type || null),
    baseLabel: isLoc ? 'Loyer annuel HT' : 'Prix net vendeur',
    base: computed.base,
    loyerAnnuel,
    pnv: computed.pnv,
    ht: computed.ht,
    tva_taux: tva,
    tva_montant: computed.tva_montant,
    ttc: computed.ttc,
    pct: computed.pct,
    pctBaseLabel: isLoc ? 'du loyer annuel HT' : 'du prix net vendeur',
    // PRIX FAI : calculé (net + TTC), le FAI stocké ne sert que de repli
    fai: computed.fai ?? faiStocke,
    hasAny,
  };
}

/**
 * Prix à mettre en avant selon qui paie les honoraires (KPI + fiche publique) :
 *   charge acquéreur → le prix du bien (net vendeur), la commission est annoncée à part
 *   charge vendeur, partagés ou non renseignée → le prix FAI (tout compris)
 */
export function prixAffichePublic(hv) {
  if (!hv || hv.isLoc) return null;
  if (isChargeAcquereur(hv.charge) && hv.pnv != null) return { value: hv.pnv, label: 'Prix du bien', isFai: false };
  if (hv.fai != null) return { value: hv.fai, label: 'Prix FAI', isFai: true };
  return null;
}

/** Mention publique « Honoraires à la charge de … » (jamais de montant). */
export const honorairesChargeMention = (charge, isLoc) => {
  const v = String(charge || '').toLowerCase();
  if (!v) return null;
  const who = isLoc
    ? (v === 'acquereur' || v === 'preneur' ? 'du preneur' : (v === 'vendeur' || v === 'bailleur' ? 'du bailleur' : `de : ${charge}`))
    : (v === 'acquereur' ? "de l'acquéreur" : (v === 'vendeur' ? 'du vendeur' : `de : ${charge}`));
  return `Honoraires à la charge ${who}`;
};

export const formatEuroFR = (n, { decimals = 0 } = {}) => {
  const v = num(n);
  if (v == null) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(v);
};

export const formatPercentFR = (n) => {
  const v = num(n);
  if (v == null) return null;
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v)}\u00A0%`;
};

/**
 * lot1 — Libellé du prix « tout compris », SOURCE UNIQUE.
 *
 * « FAI » (frais d'agence inclus) ne vaut que si le VENDEUR paie les honoraires : le prix
 * affiché les contient déjà. Quand l'ACQUÉREUR (ou le preneur) paie, il verse le prix PLUS
 * les honoraires — « FAI » induit alors en erreur.
 * Toute nouvelle mention à l'écran doit passer par ici, jamais par une chaîne en dur.
 */
export const libellePrixTotal = (honorairesCharge) => (
  isChargeAcquereur(honorairesCharge) ? 'Prix total à payer' : 'Prix FAI'
);

/** Suffixe pour une mention en ligne : « soit 70 200 € au total » / « … FAI ». */
export const suffixePrixTotal = (honorairesCharge) => (
  isChargeAcquereur(honorairesCharge) ? 'au total' : 'FAI'
);

export default deriveHonorairesView;
